import {Component, computed, input, output} from '@angular/core';
import {DatePipe} from '@angular/common';
import {FiscalJobDetail, FiscalJobItem, FiscalJobSummary} from '../_models/fiscal-jobs/fiscal-job.models';
import {exportItemStateLabel, exportItemTone, exportRunStateLabel, ExportTone} from
    '../_models/exports/data-export.models';
import {
  formatTseExportPeriod,
  isTseExportDownloadable,
  parseTseExportPeriod,
  TSE_DEVICE_LABEL,
  tseExportErrorMessage,
  tseExportItemReason
} from '../_models/exports/tse-export.models';

/**
 * One TSE data export on screen (#776): what period it covers, when it was requested, how far it got, and —
 * once it is finished — the button that hands over the file.
 *
 * ### Why a card per run and an entry per security device
 *
 * A run over a period in which the location replaced its security device produces one file per device,
 * because an export is taken from one device at a time. The engine gives each of them its own state, its own
 * error and its own retry, and this card shows them that way rather than averaging them into one line: „zwei
 * von drei fertig" is not a state anybody can act on, while „Sicherheitseinrichtung (Teil 2 von 3): gelöscht"
 * is.
 *
 * ### A named gap is the point, not a defect
 *
 * A security device that was deleted still gets an entry, and it settles with that reason. So a run over a
 * period the location cannot fully produce says so, in a row, instead of quietly containing one file fewer —
 * which for an artefact whose value is that it covers the period completely would be the worst possible
 * shape.
 *
 * ### Why the labels come from `data-export.models`
 *
 * The run and item STATE labels and tones belong to the fiscal job engine, not to either export, so both
 * screens read the same ones. Everything that is specific to this export — its period, its error sentences,
 * what an entry is called — comes from `tse-export.models`.
 *
 * ### Presentational, deliberately
 *
 * It holds no store, makes no request and decides nothing. That is what lets the page own "which run is open"
 * and "which download is in flight" in one place, and it is what makes this component renderable in Storybook
 * in every state including the ones that need a broken far end.
 */
@Component({
  selector: 'legal-tse-export-run-card',
  standalone: true,
  imports: [DatePipe],
  templateUrl: './tse-export-run-card.component.html'
})
export class TseExportRunCardComponent {
  /** The run, as the list read it. */
  readonly run = input.required<FiscalJobSummary>();

  /**
   * The run with its entries, when this is the opened one — otherwise `null`.
   *
   * Separate from {@link run} because the list carries summaries and only the opened run is read in full. A
   * card that inferred its entries from the summary's counts would be showing numbers rather than outcomes.
   */
  readonly detail = input<FiscalJobDetail | null>(null);

  /** Whether the entries are shown. */
  readonly expanded = input(false);

  /** The entry whose file is being collected right now, or `null`. */
  readonly downloadingItemId = input<string | null>(null);

  /** The entry being re-run right now, or `null`. */
  readonly retryingItemId = input<string | null>(null);

  /** The operator asked to open or close this run. Carries the run's id. */
  readonly toggled = output<string>();

  /** The operator asked for an entry's file. */
  readonly download = output<FiscalJobItem>();

  /** The operator asked for one entry to be produced again. */
  readonly retry = output<FiscalJobItem>();

  /**
   * The period this run covers, or null when its parameters could not be read.
   *
   * Read off {@link run} — the SUMMARY — never off {@link detail}. A card is rendered collapsed for every run
   * in the list and its entries are loaded only when it is opened, so taking the period from the detail model
   * would make every row say „Zeitraum unbekannt" until it was expanded (#770, live screen).
   */
  protected readonly period = computed(() => parseTseExportPeriod(this.run().parameters));

  /** The period as words. Falls back to „Zeitraum unbekannt" rather than to an empty line. */
  protected readonly periodLabel = computed(() => formatTseExportPeriod(this.period()));

  /** The run's state as words. */
  protected readonly stateLabel = computed(() => exportRunStateLabel(this.run().state));

  /** The entries of the opened run, or an empty list. */
  protected readonly items = computed<FiscalJobItem[]>(() => this.detail()?.items ?? []);

  /** The panel's id, so the header button can point `aria-controls` at it. */
  protected readonly panelId = computed(() => `tse-export-run-${this.run().id}`);

  /**
   * The run's own sentence, when the run **as a whole** could not be started.
   *
   * Keyed on the run's own `errorCode`, not on `failed` alone (Bug #789). `failed` now also covers the run
   * whose entries every one of them failed — the exact case this screen met first, three security devices all
   * refused — and such a run carries no run-level code, because its reasons belong to its entries.
   */
  protected readonly runError = computed(() => {
    const code = this.run().errorCode;
    return this.run().state === 'failed' && code ? tseExportErrorMessage(code) : null;
  });

  /** Whether the run is still being produced — what the „läuft" hint and the pulse ride on. */
  protected readonly running = computed(() =>
    this.run().state === 'queued' || this.run().state === 'running');

  /** What an entry covers. Never the device's id: an identifier answers nobody's question. */
  protected label(item: FiscalJobItem): string {
    return item.label ?? TSE_DEVICE_LABEL;
  }

  /** An entry's state as words. */
  protected itemLabel(item: FiscalJobItem): string {
    return exportItemStateLabel(item.state);
  }

  /** The tone an entry's chip is drawn in. */
  protected tone(item: FiscalJobItem): ExportTone {
    return exportItemTone(item.state);
  }

  /**
   * Why an entry is not finished, in German.
   *
   * The precedence lives in {@link tseExportItemReason} rather than here, so this screen and the DSFinV-K
   * export screen cannot answer the same question differently — which is how both came to render the
   * server's English `ErrorCatalog` description (Bug #789). That string no longer arrives.
   *
   * `errorDetail` stays FIRST, and this screen is the reason: „Diese Sicherheitseinrichtung wurde gelöscht"
   * is the handler's own wording, it rides the generic `E_FISCAL_JOB_OPERATION_FAILED` like every other
   * translated refusal, and preferring the code would replace it with „Sie können ihn einzeln erneut
   * versuchen".
   */
  protected reason(item: FiscalJobItem): string | null {
    if (item.state === 'completed' || item.state === 'pending' || item.state === 'working') return null;
    return tseExportItemReason(item);
  }

  /** Whether an entry has a file to collect. */
  protected downloadable(item: FiscalJobItem): boolean {
    return isTseExportDownloadable(item);
  }
}
